<script>
import Portal from "../../layouts/portal";
import { clienteComputed } from "@/state/helpers";
import { cargarNegocios, enlaceMapa, enlaceWhatsapp } from "@/services/portal";

export default {
  page: { title: "Negocios" },
  components: { Portal },
  data() {
    return {
      negocios: [],
      busqueda: "",
      giro: "todos",
      cargando: true,
      error: null,
      seleccionado: null,
      mostrarDetalle: false,
    };
  },
  computed: {
    ...clienteComputed,
    giros() {
      const set = new Set(this.negocios.map((n) => n.giro).filter(Boolean));
      return [
        { value: "todos", text: "Todos los giros" },
        ...Array.from(set).sort().map((g) => ({ value: g, text: g })),
      ];
    },
    negociosFiltrados() {
      const q = this.busqueda.trim().toLowerCase();
      return this.negocios
        .filter((n) => this.giro === "todos" || n.giro === this.giro)
        .filter((n) => {
          if (!q) return true;
          return [n.nombre, n.giro, n.promocion, n.direccion, n.tags]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));
        });
    },
  },
  methods: {
    mapa: enlaceMapa,
    whatsapp(negocio) {
      return enlaceWhatsapp(
        negocio.contacto,
        `Hola ${negocio.nombre || ""}, los encontré en el directorio de Recnet.`
      );
    },
    verDetalle(negocio) {
      this.seleccionado = negocio;
      this.mostrarDetalle = true;
    },
    async cargar() {
      this.cargando = true;
      this.error = null;
      try {
        this.negocios = await cargarNegocios();
      } catch (err) {
        console.error("[portal] error al cargar negocios:", err);
        this.error = "No pudimos cargar el directorio. Inténtalo de nuevo en un momento.";
      } finally {
        this.cargando = false;
      }
    },
  },
  mounted() {
    // El directorio es abierto para cualquier miembro con cuenta: ver los
    // comercios y sus promociones no exige membresía activa (a diferencia de
    // las Sesiones, que sí la piden).
    this.cargar();
  },
};
</script>

<template>
  <Portal>
    <h1 class="h3 mb-1">Negocios de la red</h1>
    <p class="text-muted">Comercios de la comunidad Recnet y sus promociones.</p>

    <div>
      <div class="d-flex flex-wrap align-items-center mb-4">
        <b-form-input v-model="busqueda" type="search" placeholder="Buscar negocio, giro o promoción…"
          class="portal-buscador mr-3 mb-2" />
        <b-form-select v-model="giro" :options="giros" class="portal-selector mb-2" />
      </div>

      <b-alert :show="!!error" variant="warning">{{ error }}</b-alert>
      <p v-if="cargando" class="text-muted">Cargando negocios…</p>
      <p v-else-if="negociosFiltrados.length === 0" class="text-muted">
        No hay negocios que coincidan con tu búsqueda.
      </p>

      <b-row v-else>
        <b-col v-for="negocio in negociosFiltrados" :key="negocio.id" md="6" xl="4" class="mb-4">
          <div class="negocio-card">
            <div class="negocio-encabezado">
              <img v-if="negocio.img" :src="negocio.img" :alt="negocio.nombre" class="negocio-logo" />
              <div v-else class="negocio-logo negocio-logo-vacio"><i class="mdi mdi-storefront"></i></div>
              <div class="negocio-identidad">
                <h3 class="negocio-nombre">{{ negocio.nombre }}</h3>
                <p class="negocio-giro mb-0">{{ negocio.giro || 'Sin giro' }}</p>
              </div>
            </div>

            <p v-if="negocio.promocion" class="negocio-promocion">
              <i class="mdi mdi-tag-outline"></i> {{ negocio.promocion }}
            </p>

            <div class="negocio-acciones">
              <b-button size="sm" variant="outline-primary" @click="verDetalle(negocio)">Ver más</b-button>
              <a v-if="whatsapp(negocio)" :href="whatsapp(negocio)" target="_blank" rel="noopener noreferrer"
                class="btn btn-sm btn-outline-success">
                <i class="mdi mdi-whatsapp"></i>
              </a>
              <a v-if="mapa(negocio)" :href="mapa(negocio)" target="_blank" rel="noopener noreferrer"
                class="btn btn-sm btn-outline-secondary">
                <i class="mdi mdi-map-marker"></i>
              </a>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <b-modal v-model="mostrarDetalle" size="lg" centered hide-footer
      :title="seleccionado ? seleccionado.nombre : ''">
      <div v-if="seleccionado">
        <div v-if="seleccionado.img" class="negocio-detalle-logo mb-3">
          <img :src="seleccionado.img" :alt="seleccionado.nombre" />
        </div>

        <p v-if="seleccionado.promocion" class="negocio-promocion">
          <i class="mdi mdi-tag-outline"></i> {{ seleccionado.promocion }}
        </p>

        <div v-if="seleccionado.descripcion" class="quienes-somos mb-3">
          <h6 class="text-muted mb-1">Quiénes somos</h6>
          <p class="mb-0">{{ seleccionado.descripcion }}</p>
        </div>

        <ul class="list-unstyled mb-3">
          <li v-if="seleccionado.giro" class="mb-2"><strong>Giro:</strong> {{ seleccionado.giro }}</li>
          <li v-if="seleccionado.horario" class="mb-2"><strong>Horario:</strong> {{ seleccionado.horario }}</li>
          <li v-if="seleccionado.direccion" class="mb-2">
            <strong>Dirección:</strong> {{ seleccionado.direccion }}
          </li>
          <li v-if="seleccionado.contacto" class="mb-2">
            <strong>Contacto:</strong> {{ seleccionado.contacto }}
          </li>
          <li v-if="seleccionado.nombreEmpresario" class="mb-2">
            <strong>Atiende:</strong> {{ seleccionado.nombreEmpresario }}
          </li>
        </ul>

        <div class="d-flex flex-wrap">
          <a v-if="whatsapp(seleccionado)" :href="whatsapp(seleccionado)" target="_blank" rel="noopener noreferrer"
            class="btn btn-success mr-2 mb-2">
            <i class="mdi mdi-whatsapp"></i> WhatsApp
          </a>
          <a v-if="mapa(seleccionado)" :href="mapa(seleccionado)" target="_blank" rel="noopener noreferrer"
            class="btn btn-outline-secondary mr-2 mb-2">
            <i class="mdi mdi-map-marker"></i> Cómo llegar
          </a>
          <a v-if="seleccionado.instagram" :href="seleccionado.instagram" target="_blank" rel="noopener noreferrer"
            class="btn btn-outline-primary mr-2 mb-2"><i class="mdi mdi-instagram"></i></a>
          <a v-if="seleccionado.facebook" :href="seleccionado.facebook" target="_blank" rel="noopener noreferrer"
            class="btn btn-outline-primary mr-2 mb-2"><i class="mdi mdi-facebook"></i></a>
        </div>
      </div>
    </b-modal>
  </Portal>
</template>

<style scoped>
.portal-buscador {
  max-width: 320px;
}

.portal-selector {
  max-width: 240px;
}

.negocio-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.negocio-encabezado {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.negocio-logo {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  /* `contain` y no `cover`: un logo apaisado o vertical perdería los bordes
     al recortarse en un cuadro. El fondo claro rellena lo que sobre. */
  object-fit: contain;
  background: #f8f9fa;
  padding: 4px;
  margin-right: 12px;
  flex-shrink: 0;
}

.negocio-logo-vacio {
  background: #ede9f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b2e83;
  font-size: 24px;
}

.negocio-nombre {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 2px;
}

.negocio-giro {
  color: #74788d;
  font-size: 0.85rem;
}

.negocio-promocion {
  background: #fff8e6;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.9rem;
  color: #8a6d1f;
  flex: 1;
}

.negocio-acciones {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

/*
 * Lo que sube el comercio es un logotipo, no una fotografía.
 *
 * Antes se estiraba al ancho del modal con `object-fit: cover`: a un logo
 * pequeño lo ampliaba —de ahí que se viera borroso y pixelado— y además le
 * recortaba los bordes.
 *
 * `contain` lo muestra completo, y dejar `width/height` en `auto` con topes
 * máximos impide que se agrande más allá de su tamaño real: si el archivo es
 * chico, se ve chico pero nítido.
 */
.negocio-detalle-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  min-height: 160px;
}

.quienes-somos {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
}

.quienes-somos p {
  white-space: pre-wrap;
  line-height: 1.5;
}

.negocio-detalle-logo img {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
}
</style>
